// src/pages/products/ProductsPage.js

import React, { useMemo, useContext } from 'react';
import GenericListPage from '../GenericListPage';
import ProductForm from './ProductForm';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import useApi from '../../hooks/useApi';

const ProductsPage = () => {
  const { settings } = useContext(AuthContext);
  const api = useApi();

  // --- CSV helpers ---
  const escapeCsv = (val) => {
    if (val == null) return '';
    return String(val).replace(/"/g, '""');
  };
  const getNested = (obj, path) => {
    if (!path) return '';
    return path.split('.').reduce((o, k) => (o ? o[k] : ''), obj);
  };
  const downloadCsvString = (csvString, filename = 'report.csv') => {
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      URL.revokeObjectURL(url);
      a.remove();
    }, 500);
  };

  // Columns for table rendering
  const productColumns = useMemo(() => {
    const currencySymbol = settings?.currencySymbol || '$';

    return [
      {
        header: 'Image',
        key: 'images',
        render: (product) => {
          const primaryImage = product.images?.find(img => img.isPrimary);
          const imageUrl = primaryImage ? primaryImage.url : "https://placehold.co/64x64/E0E7FF/6366F1?text=N/A";
          return (
            <img
              src={imageUrl}
              alt={product.name || 'Product'}
              className="w-12 h-12 object-cover rounded-md shadow-sm"
              onError={(e) => { e.target.onerror = null; e.target.src = "https://placehold.co/64x64/E0E7FF/6366F1?text=N/A"; }}
            />
          );
        }
      },
      {
        header: 'Name',
        key: 'name',
        render: (product) => (
          <div className="flex flex-col">
            <span className="font-semibold text-gray-900">{product.name}</span>
            <span className="font-mono text-xs text-gray-500">SKU: {product.sku || 'N/A'}</span>
          </div>
        )
      },
      {
        header: 'Category',
        key: 'categoryName',
        render: (product) => product.categoryName || product.categoryId?.name || 'N/A'
      },
      {
        header: 'Price',
        key: 'price',
        render: (product) => (
          <span className="font-medium text-gray-900">
            {currencySymbol}{Number(product.price || 0).toFixed(2)}
          </span>
        )
      },
      {
        header: 'Stock',
        key: 'stock',
        render: (product) => {
          const stock = product.stock || 0;
          const stockColor =
            stock > 20 ? 'bg-green-100 text-green-800' :
            stock > 0 ? 'bg-yellow-100 text-yellow-800' :
            'bg-red-100 text-red-800';
          return (
            <span className={`px-2.5 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${stockColor}`}>
              {stock} in stock
            </span>
          );
        },
      },
    ];
  }, [settings]);

  const productFilterConfig = useMemo(() => [
    {
      name: 'categoryId',
      type: 'select',
      label: 'Category',
      endpoint: 'categories',
      keyField: '_id',
      valueField: 'name',
    },
    { name: 'minPrice', type: 'number', label: 'Min Price', placeholder: '0.00' },
    { name: 'maxPrice', type: 'number', label: 'Max Price', placeholder: '99.99' },
    { name: 'minStock', type: 'number', label: 'Min Stock', placeholder: '0' },
  ], []);

  // --- CSV export handler for Products ---
  const handleDownloadCsv = async (currentFilters = {}) => {
    try {
      // Ask backend to bypass pagination
      const queryParams = { ...currentFilters, noPagination: true, export: 'csv' };
      // Call appropriate endpoint - adjust path if your API expects a different one
      const resp = await api.get('products/store-wise-stock', { queryParams });

      // Normalize rows from different possible response shapes:
      // - api hook may return axios-like { data: { data: [...] } }
      // - or return { data: [...] }
      // - or return array directly
      let rows = [];
      if (Array.isArray(resp)) rows = resp;
      else if (Array.isArray(resp?.data)) rows = resp.data;
      else if (Array.isArray(resp?.data?.data)) rows = resp.data.data;
      else if (Array.isArray(resp?.data?.rows)) rows = resp.data.rows;
      else rows = [];

      if (!rows || rows.length === 0) {
        alert('No products found for the selected filters.');
        return;
      }

      const currencySymbol = settings?.currencySymbol || '$';
      const cols = [
        { header: 'ID', key: '_id' },
        { header: 'Name', key: 'name' },
        { header: 'SKU', key: 'sku' },
        { header: 'Category', key: 'categoryName' },
        { header: 'Price', key: 'price', transform: (r) => `${Number(r.price || 0).toFixed(2)}` },
        { header: 'Stock', key: 'stock' },
        { header: 'TotalStock', key: 'totalStock' },
        { header: 'TotalValue', key: 'totalValue', transform: (r) => `${Number(r.totalValue || 0).toFixed(2)}` },
        { header: 'Created At', key: 'createdAt', transform: (r) => r.createdAt ? new Date(r.createdAt).toISOString() : '' },
      ];

      const header = cols.map(c => `"${escapeCsv(c.header)}"`).join(',');
      const body = rows.map(row => cols.map(col => {
        let v = '';
        if (typeof col.transform === 'function') v = col.transform(row);
        else if (col.key) v = getNested(row, col.key);
        return `"${escapeCsv(v ?? '')}"`;
      }).join(',')).join('\r\n');

      const csvString = `${header}\r\n${body}`;
      const filename = `products-${new Date().toISOString().slice(0,10)}.csv`;
      downloadCsvString(csvString, filename);
    } catch (err) {
      console.error('Products CSV export failed', err);
      alert('Unable to export products CSV. Check console for details.');
    }
  };

  // Header actions (example)
  const headerActions = (
    <Link to="/products/bulk-upload">
      <button className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition duration-200 text-sm font-medium">
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path></svg>
        <span>Bulk Upload</span>
      </button>
    </Link>
  );

  return (
    <GenericListPage
      title="Products"
      permission="manage_products"
      entityEndpoint="products"
      columns={productColumns}
      FormComponent={ProductForm}
      filterConfig={productFilterConfig}
      headerActions={headerActions}
      onDownloadCsv={handleDownloadCsv}
    />
  );
};

export default ProductsPage;
